/**
 * InvoiceList Component
 * 
 * Displays a list of invoices with status indicators, filtering, and sorting.
 */

import { t, getCurrentLanguage, getCurrency } from '../../i18n/translations.js';
import { escapeHtml } from '../../utils/sanitize.js';

/**
 * Invoice status configurations
 */
const STATUS_CONFIG = {
  pending: {
    label: 'status.pending',
    color: 'var(--warning)',
    bgColor: 'var(--warning-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>`,
  },
  extracted: {
    label: 'status.extracted',
    color: 'var(--info)',
    bgColor: 'var(--info-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
    </svg>`,
  },
  analyzed: {
    label: 'status.analyzed',
    color: 'var(--primary)',
    bgColor: 'var(--primary-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>`,
  },
  disputed: {
    label: 'status.disputed',
    color: 'var(--error)',
    bgColor: 'var(--error-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>`,
  },
  approved: {
    label: 'status.approved',
    color: 'var(--success)',
    bgColor: 'var(--success-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="20 6 9 17 4 12"/>
    </svg>`,
  },
  paid: {
    label: 'status.paid',
    color: 'var(--success)',
    bgColor: 'var(--success-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>`,
  },
};

// Map non-standard currency codes to ISO 4217 codes
const CURRENCY_CODE_MAP = {
  'DT': 'TND',   // Tunisian Dinar
  'DA': 'DZD',   // Algerian Dinar
  'DH': 'MAD',   // Moroccan Dirham
  'LE': 'EGP',   // Egyptian Pound
  'SR': 'SAR',   // Saudi Riyal
};

/**
 * Format currency (uses user's preferred currency as default)
 */
function formatCurrency(amount, currency = null) {
  if (amount === null || amount === undefined) return '-';
  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  let currencyCode = currency || getCurrency();
  // Map non-standard codes to ISO 4217
  currencyCode = CURRENCY_CODE_MAP[currencyCode] || currencyCode;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency || currencyCode} ${new Intl.NumberFormat(locale).format(amount)}`;
  }
}

/**
 * Format date
 */
function formatDate(dateStr) {
  if (!dateStr) return '-';
  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  return new Date(dateStr).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Check if invoice is overdue
 */
function isOverdue(dueDate, status) {
  if (!dueDate || status === 'paid' || status === 'disputed') return false;
  return new Date(dueDate) < new Date();
}

/**
 * Render status badge
 */
function renderStatusBadge(status) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return `
    <span class="status-badge" style="color: ${config.color}; background: ${config.bgColor}">
      ${config.icon}
      <span>${t(config.label) || status}</span>
    </span>
  `;
}

/**
 * Render discrepancy indicator
 */
function renderDiscrepancyIndicator(hasDiscrepancies, discrepancyCount = 0) {
  if (!hasDiscrepancies) return '';
  
  return `
    <span class="discrepancy-indicator" title="${t('invoices.discrepanciesFound') || 'Discrepancies found'}">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      ${discrepancyCount > 0 ? `<span class="count">${discrepancyCount}</span>` : ''}
    </span>
  `;
}

/**
 * Render a single invoice row
 */
function renderInvoiceRow(invoice) {
  const overdue = isOverdue(invoice.dueDate, invoice.status);
  
  return `
    <tr class="invoice-row ${overdue ? 'overdue' : ''}" data-invoice-id="${invoice.id}">
      <td class="cell-checkbox">
        <input type="checkbox" class="invoice-checkbox" value="${invoice.id}" />
      </td>
      <td class="cell-invoice-number">
        <div class="invoice-number-cell">
          <a href="/#/invoices/${invoice.id}" class="invoice-link">${escapeHtml(invoice.invoiceNumber)}</a>
          ${renderDiscrepancyIndicator(invoice.hasDiscrepancies, invoice.discrepancyCount)}
        </div>
      </td>
      <td class="cell-vendor">${escapeHtml(invoice.vendorName || '-')}</td>
      <td class="cell-date">
        <span class="${overdue ? 'overdue-date' : ''}">${formatDate(invoice.dueDate)}</span>
        ${overdue ? `<span class="overdue-badge">${t('invoices.overdue') || 'Overdue'}</span>` : ''}
      </td>
      <td class="cell-amount">${formatCurrency(invoice.total, invoice.currency)}</td>
      <td class="cell-status">${renderStatusBadge(invoice.status)}</td>
      <td class="cell-actions">
        <div class="action-buttons">
          <button class="btn-icon" onclick="window.viewInvoice('${invoice.id}')" title="${t('common.view') || 'View'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
          ${invoice.status === 'pending' || invoice.status === 'extracted' ? `
            <button class="btn-icon" onclick="window.analyzeInvoice('${invoice.id}')" title="${t('invoices.analyze') || 'Analyze'}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </button>
          ` : ''}
          <button class="btn-icon" onclick="window.showInvoiceActions('${invoice.id}', event)" title="${t('common.more') || 'More'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="1"/>
              <circle cx="19" cy="12" r="1"/>
              <circle cx="5" cy="12" r="1"/>
            </svg>
          </button>
        </div>
      </td>
    </tr>
  `;
}

/**
 * Render invoice list filters
 */
function renderFilters(activeFilters = {}) {
  return `
    <div class="invoice-filters">
      <div class="filter-group">
        <label>${t('invoices.status') || 'Status'}</label>
        <select id="filter-status" class="filter-select" onchange="window.applyInvoiceFilters()">
          <option value="">${t('common.all') || 'All'}</option>
          <option value="pending" ${activeFilters.status === 'pending' ? 'selected' : ''}>${t('status.pending') || 'Pending'}</option>
          <option value="extracted" ${activeFilters.status === 'extracted' ? 'selected' : ''}>${t('status.extracted') || 'Extracted'}</option>
          <option value="analyzed" ${activeFilters.status === 'analyzed' ? 'selected' : ''}>${t('status.analyzed') || 'Analyzed'}</option>
          <option value="disputed" ${activeFilters.status === 'disputed' ? 'selected' : ''}>${t('status.disputed') || 'Disputed'}</option>
          <option value="approved" ${activeFilters.status === 'approved' ? 'selected' : ''}>${t('status.approved') || 'Approved'}</option>
          <option value="paid" ${activeFilters.status === 'paid' ? 'selected' : ''}>${t('status.paid') || 'Paid'}</option>
        </select>
      </div>
      <div class="filter-group">
        <label>${t('invoices.vendor') || 'Vendor'}</label>
        <input type="text" id="filter-vendor" class="filter-input" placeholder="${t('common.search') || 'Search'}..." value="${activeFilters.vendor || ''}" onchange="window.applyInvoiceFilters()" />
      </div>
      <div class="filter-group">
        <label>${t('invoices.dateRange') || 'Date Range'}</label>
        <div class="date-range">
          <input type="date" id="filter-date-from" class="filter-input" value="${activeFilters.dateFrom || ''}" onchange="window.applyInvoiceFilters()" />
          <span>-</span>
          <input type="date" id="filter-date-to" class="filter-input" value="${activeFilters.dateTo || ''}" onchange="window.applyInvoiceFilters()" />
        </div>
      </div>
      <div class="filter-group filter-actions">
        <button class="btn btn-secondary btn-sm" onclick="window.clearInvoiceFilters()">
          ${t('common.clear') || 'Clear'}
        </button>
      </div>
    </div>
  `;
}

/**
 * Render empty state
 */
function renderEmptyState() {
  return `
    <div class="empty-state">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="empty-icon">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="12" y1="18" x2="12" y2="12"/>
        <line x1="9" y1="15" x2="15" y2="15"/>
      </svg>
      <h3>${t('invoices.noInvoices') || 'No invoices found'}</h3>
      <p>${t('invoices.noInvoicesDescription') || 'Upload an invoice to get started'}</p>
      <a href="#upload?type=invoice" class="btn btn-primary">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        ${t('invoices.uploadInvoice') || 'Upload Invoice'}
      </a>
    </div>
  `;
}

/**
 * Render pagination
 */
function renderPagination(pagination) {
  const { page, totalPages, totalItems } = pagination;
  
  if (totalPages <= 1) return '';
  
  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, page - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  
  return `
    <div class="pagination">
      <span class="pagination-info">
        ${t('common.showing') || 'Showing'} ${(page - 1) * 20 + 1} - ${Math.min(page * 20, totalItems)} ${t('common.of') || 'of'} ${totalItems}
      </span>
      <div class="pagination-buttons">
        <button class="btn-page" ${page === 1 ? 'disabled' : ''} onclick="window.goToInvoicePage(${page - 1})">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        ${pages.map(p => `
          <button class="btn-page ${p === page ? 'active' : ''}" onclick="window.goToInvoicePage(${p})">${p}</button>
        `).join('')}
        <button class="btn-page" ${page === totalPages ? 'disabled' : ''} onclick="window.goToInvoicePage(${page + 1})">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>
    </div>
  `;
}

/**
 * Render the full invoice list component
 */
export function renderInvoiceList(options = {}) {
  const {
    invoices = [],
    filters = {},
    pagination = { page: 1, totalPages: 1, totalItems: 0 },
    showFilters = true,
    showBulkActions = true,
  } = options;

  if (invoices.length === 0 && !showFilters) {
    return renderEmptyState();
  }

  return `
    <div class="invoice-list-container">
      <div class="invoice-list-header">
        <div class="header-left">
          <h2>${t('nav.invoices') || 'Invoices'}</h2>
          <span class="item-count">${pagination.totalItems} ${t('common.items') || 'items'}</span>
        </div>
        <div class="header-right">
          <a href="#upload?type=invoice" class="btn btn-primary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            ${t('invoices.uploadNew') || 'Upload Invoice'}
          </a>
        </div>
      </div>

      ${showFilters ? renderFilters(filters) : ''}

      ${showBulkActions ? `
        <div class="bulk-actions" id="invoice-bulk-actions" style="display: none;">
          <span class="selected-count">0 ${t('common.selected') || 'selected'}</span>
          <button class="btn btn-secondary btn-sm" onclick="window.bulkAnalyzeInvoices()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            ${t('invoices.analyzeSelected') || 'Analyze Selected'}
          </button>
          <button class="btn btn-secondary btn-sm" onclick="window.bulkExportInvoices()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            ${t('common.export') || 'Export'}
          </button>
        </div>
      ` : ''}

      ${invoices.length === 0 ? renderEmptyState() : `
        <div class="table-container">
          <table class="invoice-table">
            <thead>
              <tr>
                <th class="th-checkbox">
                  <input type="checkbox" id="select-all-invoices" onchange="window.toggleAllInvoices(this.checked)" />
                </th>
                <th class="th-sortable" data-sort="invoiceNumber">
                  ${t('invoices.number') || 'Invoice #'}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="sort-icon">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </th>
                <th class="th-sortable" data-sort="vendorName">
                  ${t('invoices.vendor') || 'Vendor'}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="sort-icon">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </th>
                <th class="th-sortable" data-sort="dueDate">
                  ${t('invoices.dueDate') || 'Due Date'}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="sort-icon">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </th>
                <th class="th-sortable" data-sort="total">
                  ${t('invoices.amount') || 'Amount'}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="sort-icon">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </th>
                <th>${t('invoices.status') || 'Status'}</th>
                <th>${t('common.actions') || 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              ${invoices.map(invoice => renderInvoiceRow(invoice)).join('')}
            </tbody>
          </table>
        </div>
        
        ${renderPagination(pagination)}
      `}
    </div>
  `;
}

/**
 * Initialize invoice list event handlers
 */
export function initInvoiceListHandlers() {
  // Select all checkbox
  window.toggleAllInvoices = (checked) => {
    const checkboxes = document.querySelectorAll('.invoice-checkbox');
    checkboxes.forEach(cb => cb.checked = checked);
    updateBulkActionsVisibility();
  };

  // Individual checkbox change
  document.querySelectorAll('.invoice-checkbox').forEach(cb => {
    cb.addEventListener('change', updateBulkActionsVisibility);
  });

  // Sort headers
  document.querySelectorAll('.th-sortable').forEach(th => {
    th.addEventListener('click', () => {
      const sortField = th.dataset.sort;
      window.sortInvoices && window.sortInvoices(sortField);
    });
  });
}

/**
 * Update bulk actions visibility
 */
function updateBulkActionsVisibility() {
  const bulkActions = document.getElementById('invoice-bulk-actions');
  const checkboxes = document.querySelectorAll('.invoice-checkbox:checked');
  
  if (bulkActions) {
    bulkActions.style.display = checkboxes.length > 0 ? 'flex' : 'none';
    const countSpan = bulkActions.querySelector('.selected-count');
    if (countSpan) {
      countSpan.textContent = `${checkboxes.length} ${t('common.selected') || 'selected'}`;
    }
  }
}

/**
 * Get selected invoice IDs
 */
export function getSelectedInvoiceIds() {
  const checkboxes = document.querySelectorAll('.invoice-checkbox:checked');
  return Array.from(checkboxes).map(cb => cb.value);
}

export { STATUS_CONFIG, formatCurrency, formatDate };
